// Campanita de notificaciones (se monta en el Header global).
// Cuenta no leídas (polling) + dropdown con la lista; marcar leídas.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CalendarClock, FileText, FlaskConical, MessageSquare, Pill } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui';
import { esModuloAusente, getErrorMessage } from '@/lib/api/client';
import { medRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Notif { id: string; tipo: string; titulo: string; mensaje: string | null; leida: boolean; created_at: string }

const ICON: Record<string, typeof Bell> = { cita: CalendarClock, examen: FlaskConical, receta: Pill, mensaje: MessageSquare, sistema: FileText };

export function NotificacionesBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // En instalaciones sin el módulo médico (TrackConstruccion) PostgREST responde 406/404:
  // no se reintenta, se deja de consultar y la campana no se muestra.
  const { data: count = 0, error: errContar } = useQuery<number>({
    queryKey: ['med-notif-contar'], queryFn: () => medRpc<number>('med_notif_contar', {}),
    retry: (n, e) => !esModuloAusente(e) && n < 3,
    refetchInterval: (q) => (esModuloAusente(q.state.error) ? false : 20000),
  });
  const { data: notifs = [] } = useQuery<Notif[]>({
    queryKey: ['med-notifs'], queryFn: () => medRpc<Notif[]>('med_notificaciones', { _solo_no_leidas: false }), enabled: open,
  });

  const invalidar = () => { void qc.invalidateQueries({ queryKey: ['med-notif-contar'] }); void qc.invalidateQueries({ queryKey: ['med-notifs'] }); };
  const leer = useMutation({ mutationFn: (id: string) => medRpc('med_notif_leer', { _id: id }), onSuccess: invalidar, onError: (e) => toast.error(getErrorMessage(e)) });
  const leerTodas = useMutation({ mutationFn: () => medRpc('med_notif_leer_todas', {}), onSuccess: invalidar, onError: (e) => toast.error(getErrorMessage(e)) });

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  if (esModuloAusente(errContar)) return null;

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="icon" title="Notificaciones" onClick={() => setOpen((v) => !v)} className="relative">
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-lg border bg-card shadow-lg z-50">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-sm font-semibold">Notificaciones</span>
            {count > 0 && <button className="text-xs text-primary hover:underline" onClick={() => leerTodas.mutate()}>Marcar todas</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifs.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Sin notificaciones</p>}
            {notifs.map((n) => {
              const Icon = ICON[n.tipo] ?? Bell;
              return (
                <button key={n.id} onClick={() => !n.leida && leer.mutate(n.id)}
                  className={`flex w-full items-start gap-2 border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted/50 ${n.leida ? 'opacity-60' : ''}`}>
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="flex-1">
                    <span className="block font-medium">{n.titulo}{!n.leida && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-red-500 align-middle" />}</span>
                    {n.mensaje && <span className="block text-xs text-muted-foreground">{n.mensaje}</span>}
                    <span className="block text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString(LOCALE)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
